/**
 * Popup "Edit Subscription" — Flow → Subscriptions (PLAN_FLOW_PRODUK.md Fasa 0–2, 30 Sept 2026).
 * Menggantikan popup customer lama dalam FlowUltraTab (slot picker Sheet, "Give this slot",
 * "Move to another slot", "Account details sent" — semuanya tidak berwayar; dibuang atas
 * keputusan user: Type 1 buka Flow sendiri melalui extension, Type 2 auto-login).
 *
 *   Atas    — customer: WhatsApp, bayaran sebenar, email
 *   Tengah  — langganan: produk, Gmail (Session Access / Type 2 + credit), mula, tamat + Change, notes
 *   Bawah   — sejarah: resit + tindakan admin (Extend / Set date)
 *
 * Gmail + credit + notes + sejarah datang dari server (functions/flowLangganan.js).
 */
import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { Badge, Btn, IconBtn } from '../design/ui';
import FlowCustomerEmailField from './FlowCustomerEmailField';
import FlowCustomerPhoneField from './FlowCustomerPhoneField';
import FlowTukarJenis2 from './FlowTukarJenis2';
import FlowKiraDari from './FlowKiraDari';
import { produkUntuk, rmDariSen } from '../services/flowProdukService';
import { muatSejarahLangganan, simpanNotaLangganan, padamLangganan, jenisRekod } from '../services/flowLanggananService';

const tarikh = (d, hari = false) => {
    const t = d ? new Date(d) : null;
    if (!t || Number.isNaN(t.getTime())) return '';
    return t.toLocaleDateString('en-MY', { ...(hari ? { weekday: 'short' } : {}), day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Kuala_Lumpur' });
};
const plural = (n, w) => `${n} ${w}${n === 1 ? '' : 's'}`;
const lalu = (ms) => {
    if (!ms) return '';
    const m = Math.round((Date.now() - ms) / 60000);
    if (m < 60) return m <= 1 ? 'just now' : `${m} minutes ago`;
    const h = Math.round(m / 60);
    if (h < 24) return `${plural(h, 'hour')} ago`;
    return `${plural(Math.round(h / 24), 'day')} ago`;
};
const SIHAT = { healthy: ['Gmail healthy', 'ok'], degraded: ['Gmail degraded', 'warn'], maintenance: ['Maintenance', 'warn'],
    rate_limited: ['Busy', 'warn'], auth_required: ['Gmail needs login', 'stop'], offline: ['Gmail offline', 'stop'], disabled: ['Gmail disabled', 'stop'] };

export default function FlowSubscriptionModal({ c, st, produk, lain = [], onOpenLain, onClose, onChanged, onChangeDate, onWhatsApp, onGoSessionAccess }) {
    const p = produkUntuk(c, produk);
    const [nota, setNota] = useState(c.note || '');
    const [notaDisimpan, setNotaDisimpan] = useState(c.note || '');
    const [notaSedang, setNotaSedang] = useState(false);
    const [notaRalat, setNotaRalat] = useState('');
    const [sejarah, setSejarah] = useState(null);
    const [sejarahRalat, setSejarahRalat] = useState('');
    const [padamBuka, setPadamBuka] = useState(false);
    const [buangResit, setBuangResit] = useState(false);
    const [padamSedang, setPadamSedang] = useState(false);
    const [padamRalat, setPadamRalat] = useState('');

    // Muat sekali setiap langganan (bukan setiap kali objek c dibina semula selepas muat semula).
    const kindId = `${jenisRekod(c)}:${c.id}`;
    useEffect(() => {
        let batal = false;
        muatSejarahLangganan(c)
            .then((r) => { if (!batal) setSejarah(r); })
            .catch((e) => { if (!batal) setSejarahRalat(e?.code === 'functions/not-found' ? 'History needs the server update to be deployed.' : (e?.message || 'Could not load history.')); });
        return () => { batal = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [kindId]);

    const simpanNota = async () => {
        setNotaSedang(true); setNotaRalat('');
        try {
            const r = await simpanNotaLangganan(c, nota);
            setNotaDisimpan(r.note); setNota(r.note);
            onChanged?.('Notes saved.');
        } catch (e) {
            setNotaRalat(e?.code === 'functions/not-found' ? 'Not saved — the server update is not deployed yet.' : `Not saved: ${e?.message || 'unknown error'}`);
        } finally { setNotaSedang(false); }
    };

    const padam = async () => {
        setPadamSedang(true); setPadamRalat('');
        try {
            const r = await padamLangganan(c, { buangSejarah: buangResit });
            onChanged?.(r?.gmailFreed ? `Customer deleted. Gmail ${r.gmailFreed} is now free.` : 'Customer deleted.', true, true);
        } catch (e) {
            setPadamRalat(e?.code === 'functions/not-found' ? 'Not deleted — the server update is not deployed yet.' : `Not deleted: ${e?.message || 'unknown error'}`);
        } finally { setPadamSedang(false); }
    };

    const g = c.gmail;
    const sihat = g?.kind === 'session' ? (SIHAT[g.status] || [g.status || 'Unknown status', 'neutral']) : null;
    const peratus = g?.kind === 't2' && g.had ? Math.min(100, Math.round((g.dahGuna / g.had) * 100)) : 0;
    // Gmail benar-benar dilepaskan HANYA bila: Type 2 (1:1), atau Type 1 tanpa langganan Type 1 lain.
    const gmailName = g && (g.kind === 'session' || g.kind === 't2') ? (g.gmail || g.label || '') : '';
    const akanLepas = !!gmailName && (c.jenis === 2 || !lain.some((x) => (x.jenis === 2 ? 2 : 1) === 1));

    return (
        <>
        <div className="fu-modal-bg" onClick={onClose}>
            <div className="fu-modal" onClick={(e) => e.stopPropagation()}>
                <div className="fu-modal__h">
                    <div>
                        <p className="s-cardname">{c.name || c.phone || c.email}</p>
                        <p className="s-dim">{c.email || 'No email saved'}</p>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <Badge tone={st.tone}>{st.label}</Badge>
                        <IconBtn label="Close" icon={<X size={15} />} onClick={onClose} />
                    </div>
                </div>

                <div className="fu-modal__b">
                    {/* ── Customer ── */}
                    <div className="s-kv">
                        {/* Customer manual: nombor boleh ditambah / dibetulkan di sini (Add customer terima email sahaja). */}
                        <FlowCustomerPhoneField key={'tel-' + c.id} customer={c} onWhatsApp={onWhatsApp}
                            onSaved={() => onChanged?.('WhatsApp number saved.', true)} />
                        <div>
                            <p className="s-kv__lab">Paid</p>
                            <div className="s-kv__box">
                                {c.paidSen ? rmDariSen(c.paidSen) : <span className="s-dim">—</span>}
                                <span className="s-dim" style={{ marginLeft: 'auto', fontSize: 12.5 }}>
                                    {c.manual ? 'Manual' : 'Billplz'}{c.paidAt ? ` · ${tarikh(c.paidAt)}` : ''}
                                </span>
                            </div>
                        </div>
                    </div>

                    <FlowCustomerEmailField key={c.id} customer={c} onSaved={() => onChanged?.('Email saved.', true)} />

                    {/* ── Langganan ── */}
                    {c.manual && c.jenis !== 2 ? (
                        <FlowTukarJenis2 key={'t2-' + c.id} customer={c} label={p ? `${p.name} · Type 1` : undefined}
                            produkJenis2={produk.filter((x) => x.jenis === 2 && x.active !== false)}
                            onDone={(vaultId) => onChanged?.(`Changed to Type 2. Store this Gmail's password + 2FA key in the vault as gmail-${vaultId}.`, true, true)} />
                    ) : (
                        <div>
                            <p className="s-kv__lab">Product</p>
                            <div className="s-kv__box">
                                {p ? p.name : (c.productName || 'Flow Ultra')}
                                <span className="s-dim" style={{ marginLeft: 'auto' }}>Type {c.jenis === 2 ? 2 : 1}</span>
                            </div>
                        </div>
                    )}

                    {/* B1 (keputusan user 3 Okt): satu baris = satu langganan. Orang yang sama
                        dengan produk lain ditunjuk di sini supaya admin tidak perlu mencari. */}
                    {lain.length > 0 && (
                        <div>
                            <p className="s-kv__lab">Other subscriptions of this customer</p>
                            {lain.map((x) => (
                                <div key={jenisRekod(x) + x.id} className="s-kv__box" style={{ marginBottom: 6 }}>
                                    {produkUntuk(x, produk)?.name || `Type ${x.jenis === 2 ? 2 : 1}`}
                                    <span className="s-dim" style={{ fontSize: 12.5 }}>{x.expiresAt ? ` · until ${tarikh(x.expiresAt)}` : ''}</span>
                                    <span style={{ marginLeft: 'auto' }}>
                                        <Btn size="sm" variant="ghost" onClick={() => onOpenLain(x)}>Open</Btn>
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}

                    <div>
                        <p className="s-kv__lab">Google account</p>
                        {g?.kind === 'session' && (
                            <div className="s-kv__box" style={{ flexWrap: 'wrap' }}>
                                {g.gmail || g.label || g.accountId}
                                <span style={{ marginLeft: 'auto' }}><Badge tone={sihat[1]}>{sihat[0]}</Badge></span>
                            </div>
                        )}
                        {g?.kind === 'session' && (
                            <p className="s-kv__hint">
                                Via Session Access · {g.accountId}
                                {' · '}{g.lastUsedAt ? `last opened Flow ${lalu(g.lastUsedAt)}` : 'has not opened Flow yet'}
                            </p>
                        )}
                        {g?.kind === 't2' && (
                            <div className="s-kv__box" style={{ display: 'block' }}>
                                <div>{g.gmail}</div>
                                {g.had ? (
                                    <>
                                        <div style={{ margin: '8px 0 4px', height: 6, borderRadius: 3, background: 'var(--s-fill)', overflow: 'hidden' }}>
                                            <div style={{ width: `${peratus}%`, height: '100%', background: peratus >= 90 ? 'var(--s-stop)' : 'var(--s-brand)' }} />
                                        </div>
                                        <span className="s-dim" style={{ fontSize: 12.5 }}>
                                            {Math.round(g.dahGuna).toLocaleString('en-MY')} / {g.had.toLocaleString('en-MY')} credits used
                                            {g.kreditPada ? ` · updated ${lalu(g.kreditPada)}` : ' · not read yet'}
                                        </span>
                                    </>
                                ) : <span className="s-dim" style={{ fontSize: 12.5 }}>No credit limit set</span>}
                            </div>
                        )}
                        {g?.kind === 'text' && (
                            <>
                                <div className="s-kv__box">{g.text}</div>
                                <p className="s-kv__hint">Typed by hand — not linked to Session Access.</p>
                            </>
                        )}
                        {!g && (
                            <>
                                <div className="s-kv__box" style={{ color: 'var(--s-stop)' }}>
                                    {c.sessionPending ? 'Paid — waiting for a free Gmail' : 'No Gmail yet'}
                                    {onGoSessionAccess && (
                                        <span style={{ marginLeft: 'auto' }}>
                                            <Btn size="sm" variant="ghost" onClick={onGoSessionAccess}>Session Access</Btn>
                                        </span>
                                    )}
                                </div>
                                <p className="s-kv__hint">
                                    {c.uid ? 'Give this customer a Gmail in Session Access.'
                                        : 'No SESAAT account matches this customer\'s email or WhatsApp number. Save their email first, then give a Gmail in Session Access.'}
                                </p>
                            </>
                        )}
                    </div>

                    {g?.kind === 't2' && (
                        <FlowKiraDari key={'kd-' + c.id} uid={c.uid} kiraDari={g.kiraDari} endAt={g.endAt}
                            onSaved={() => onChanged?.('Credit count reset and recounted.', true)} />
                    )}

                    <div className="s-kv">
                        <div>
                            <p className="s-kv__lab">Started</p>
                            <div className="s-kv__box">{tarikh(c.startedAt, true) || <span className="s-dim">Not recorded</span>}</div>
                        </div>
                        <div>
                            <p className="s-kv__lab">{c.jenis === 2 ? 'Family renews (access ends)' : 'Expires'}</p>
                            <div className="s-kv__box">
                                {tarikh(c.expiresAt, true) || <span className="s-dim">Not recorded</span>}
                                <span style={{ marginLeft: 'auto' }}>
                                    <Btn size="sm" variant="ghost" onClick={() => onChangeDate(c)}>Change</Btn>
                                </span>
                            </div>
                            {c.expiresAt && (
                                <p className="s-kv__hint" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    {c.days < 0 ? `${plural(Math.abs(c.days), 'day')} overdue` : `${plural(c.days, 'day')} left`}
                                    {c.days <= 7 && <Badge tone={c.days < 0 ? 'stop' : 'warn'}>{c.days < 0 ? 'Overdue' : 'Soon'}</Badge>}
                                </p>
                            )}
                        </div>
                    </div>

                    <div>
                        <p className="s-kv__lab">Notes</p>
                        <textarea className="s-inp" rows={2} value={nota} maxLength={1000}
                            placeholder="Friend rate, asked for a second slot" onChange={(e) => setNota(e.target.value)} />
                        {(nota.trim() !== notaDisimpan.trim() || notaRalat) && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
                                {notaRalat && <span style={{ color: 'var(--s-stop)', fontSize: 12.5 }}>{notaRalat}</span>}
                                <span style={{ marginLeft: 'auto' }}>
                                    <Btn size="sm" onClick={simpanNota} disabled={notaSedang}>{notaSedang ? 'Saving…' : 'Save notes'}</Btn>
                                </span>
                            </div>
                        )}
                    </div>

                    {/* ── Sejarah ── */}
                    <div>
                        <p className="s-kv__lab">History</p>
                        <div style={{ border: '1px solid var(--s-line)', borderRadius: 8, padding: '8px 10px', fontSize: 13 }}>
                            {sejarahRalat ? <span style={{ color: 'var(--s-stop)' }}>{sejarahRalat}</span>
                                : !sejarah ? <span className="s-dim">Loading…</span>
                                    : (() => {
                                        const item = [
                                            ...sejarah.orders.filter((o) => o.status === 'paid').map((o) => ({
                                                at: o.at, key: 'o' + o.id,
                                                teks: <>{o.renewal ? 'Renewed' : 'Paid'} {rmDariSen(o.amountSen)}{o.productName ? ` · ${o.productName}` : ''}{o.days ? ` · ${o.days} days` : ''}
                                                    {/* Resit manual (Extend days → Paid renewal, 2026-10-14) — tiada pautan resit Billplz. */}
                                                    {o.provider === 'manual' ? ' · manual (transfer)' : ''}
                                                    {o.receipt && <> · <a href={o.receipt} target="_blank" rel="noopener noreferrer">receipt</a></>}</>,
                                            })),
                                            ...sejarah.actions.map((a) => ({
                                                at: a.at, key: 'a' + a.id,
                                                teks: <>{a.mode === 'set' ? 'Expiry set' : `Extended ${plural(a.days || 0, 'day')}`}{a.from && a.to ? `: ${tarikh(a.from)} → ${tarikh(a.to)}` : ''}
                                                    {a.reason ? <span className="s-dim"> · “{a.reason}”</span> : null}</>,
                                            })),
                                            ...(c.manual ? [{ at: (c.manualPaidAt || c.createdAt || c.startedAt)?.getTime?.(), key: 'm', teks: <>Added manually{c.manualSen ? ` · paid ${rmDariSen(c.manualSen)}` : ''}</> }] : []),
                                        ].sort((x, y) => (y.at || 0) - (x.at || 0));
                                        if (!item.length) return <span className="s-dim">Nothing yet.</span>;
                                        return item.map((x) => (
                                            <div key={x.key} style={{ display: 'flex', gap: 10, padding: '3px 0' }}>
                                                <span className="s-dim s-num" style={{ flex: 'none', width: 92 }}>{x.at ? tarikh(x.at) : '—'}</span>
                                                <span style={{ minWidth: 0 }}>{x.teks}</span>
                                            </div>
                                        ));
                                    })()}
                        </div>
                    </div>
                </div>

                <div className="fu-modal__f" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Btn variant="ghost" onClick={() => { setPadamRalat(''); setBuangResit(false); setPadamBuka(true); }}
                        style={{ color: 'var(--s-stop)' }}>Delete</Btn>
                    <Btn variant="quiet" onClick={onClose} style={{ marginLeft: 'auto' }}>Close</Btn>
                </div>
            </div>
        </div>

        {/* The confirm box must sit ABOVE the popup that opened it: .fu-modal-bg is z-index 80 (FlowUltraTab.css). */}
        {padamBuka && (
            <div className="fu-modal-bg" style={{ zIndex: 90 }} onClick={() => !padamSedang && setPadamBuka(false)}>
                <div className="fu-modal" style={{ maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
                    <div className="fu-modal__h">
                        <div><p className="s-cardname">Delete customer</p></div>
                        <IconBtn label="Close" icon={<X size={15} />} onClick={() => !padamSedang && setPadamBuka(false)} />
                    </div>
                    <div className="fu-modal__b">
                        <p style={{ fontSize: 14, lineHeight: 1.5 }}>
                            {akanLepas
                                ? <>Delete <b>{c.name || c.phone || c.email}</b>? The Gmail <b>{gmailName}</b> will be released and can be given to someone else.</>
                                : <>Remove <b>{c.name || c.phone || c.email}</b> from the list?</>}
                        </p>
                        <p className="s-kv__hint" style={{ color: 'var(--s-stop)', marginTop: 4 }}>This can’t be undone.</p>
                        <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginTop: 12, fontSize: 13, cursor: 'pointer' }}>
                            <input type="checkbox" checked={buangResit} disabled={padamSedang}
                                onChange={(e) => setBuangResit(e.target.checked)} style={{ marginTop: 2 }} />
                            <span>Also delete payment records (for test or mistake entries)</span>
                        </label>
                        {padamRalat && <p style={{ color: 'var(--s-stop)', fontSize: 12.5, marginTop: 10 }}>{padamRalat}</p>}
                    </div>
                    <div className="fu-modal__f" style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                        <Btn variant="quiet" onClick={() => setPadamBuka(false)} disabled={padamSedang}>Cancel</Btn>
                        <Btn onClick={padam} disabled={padamSedang}
                            style={{ background: 'var(--s-stop)', borderColor: 'var(--s-stop)', color: '#fff' }}>
                            {padamSedang ? 'Deleting…' : 'Delete customer'}
                        </Btn>
                    </div>
                </div>
            </div>
        )}
        </>
    );
}
